import { useEffect, useState, type ReactNode } from "react";

import {
  CUSTOM_OPTIONAL,
  CUSTOM_REQUIRED,
  EMPTY_DRAFT,
  draftOf,
  readCustomFood,
  type CustomFoodDraft,
  type NutrientKey,
} from "../domain/customFoods";
import type { CustomFood } from "../domain/types";
import { useKaya } from "../store/kayaStore";
import { addCustomFood, editCustomFood, removeCustomFood } from "../store/toggles";
import { ActionBar } from "./ActionBar";
import { ConfirmSheet } from "./ConfirmSheet";
import { DishForm } from "./DishSheet";
import { ChoiceField, Field, FieldGrid, FieldSet } from "./Fields";
import { I } from "./icons";
import { NavRow, RowGroup } from "./Rows";
import { Segmented } from "./Segmented";
import { Sheet } from "./Sheet";

const LABEL: Record<NutrientKey, { label: string; unit: string }> = {
  k: { label: "Energy", unit: "kcal" },
  p: { label: "Protein", unit: "g" },
  c: { label: "Carbohydrate", unit: "g" },
  f: { label: "Fat", unit: "g" },
  fb: { label: "Fibre", unit: "g" },
  sf: { label: "Saturated fat", unit: "g" },
  na: { label: "Sodium", unit: "mg" },
  ca: { label: "Calcium", unit: "mg" },
  fe: { label: "Iron", unit: "mg" },
  pot: { label: "Potassium", unit: "mg" },
};

const one = (n: number) => (Math.round(n * 10) / 10).toLocaleString();

/**
 * Add a food the table lacks, or edit one added before (ADR-032).
 *
 * A label prints its values per 100 g and per serving, and people copy whichever is bigger, so
 * the sheet asks which one they typed rather than guessing; `readCustomFood` restates a serving's
 * figures per 100 g on save. The four that every total needs are required; the rest stay absent
 * when blank, never 0 (invariant 18). An edit is a tombstone plus a new id, so entries already
 * logged from the old version keep what they copied.
 */
export function CustomFoodSheet({
  open,
  onClose,
  initial,
  prefillName,
  onSaved,
}: {
  open: boolean;
  onClose: () => void;
  initial?: CustomFood | null;
  prefillName?: string;
  onSaved?: (id: string) => void;
}) {
  if (!open) return null;
  // A saved food opens as what it is; a new one asks first (board FoodBatchDish, ADR-048).
  if (initial?.dish) return <DishForm key={initial.id} initial={initial} onClose={onClose} onSaved={onSaved} />;
  if (initial)
    return <CustomFoodForm key={initial.id} onClose={onClose} initial={initial} onSaved={onSaved} />;
  return <NewFood prefillName={prefillName} onClose={onClose} onSaved={onSaved} />;
}

/** A new food: from a label, or a dish cooked at home — one question, then that sheet. */
function NewFood({
  prefillName,
  onClose,
  onSaved,
}: {
  prefillName?: string;
  onClose: () => void;
  onSaved?: (id: string) => void;
}) {
  const [kind, setKind] = useState<"label" | "dish">("label");
  const kindSwitch = (
    <ChoiceField label="What is it?">
      <Segmented
        ariaLabel="What is it?"
        value={kind}
        onChange={setKind}
        options={[
          { value: "label", label: "From a label" },
          { value: "dish", label: "A dish I cook" },
        ]}
      />
    </ChoiceField>
  );
  return kind === "dish" ? (
    <DishForm initial={null} prefillName={prefillName} kindSwitch={kindSwitch} onClose={onClose} onSaved={onSaved} />
  ) : (
    <CustomFoodForm initial={null} prefillName={prefillName} kindSwitch={kindSwitch} onClose={onClose} onSaved={onSaved} />
  );
}

function CustomFoodForm({
  onClose,
  initial,
  prefillName,
  kindSwitch,
  onSaved,
}: {
  onClose: () => void;
  kindSwitch?: ReactNode;
  initial: CustomFood | null;
  prefillName?: string;
  onSaved?: (id: string) => void;
}) {
  const { update, showToast } = useKaya();
  const [d, setD] = useState<CustomFoodDraft>(() =>
    initial ? draftOf(initial) : { ...EMPTY_DRAFT, name: prefillName?.trim() ?? "" }
  );
  const [asking, setAsking] = useState(false);

  useEffect(() => {
    if (initial) return;
    // The name box is where a new food starts; this sheet may sit over the picker, so the input
    // is found in the topmost pane rather than by an id the Field keeps to itself.
    const t = window.setTimeout(() => {
      const panes = document.querySelectorAll<HTMLElement>(".k-sheet__pane");
      panes[panes.length - 1]?.querySelector<HTMLInputElement>("input")?.focus();
    }, 0);
    return () => window.clearTimeout(t);
  }, [initial]);

  const set = <K extends keyof CustomFoodDraft>(k: K, v: CustomFoodDraft[K]) => setD((x) => ({ ...x, [k]: v }));
  const setV = (k: NutrientKey, v: string) => setD((x) => ({ ...x, values: { ...x.values, [k]: v } }));

  const read = readCustomFood(d);
  const unchanged =
    initial !== null &&
    read.food !== undefined &&
    JSON.stringify(read.food) ===
      JSON.stringify({ name: initial.name, unit: initial.unit, g: initial.g, per100: initial.per100 });
  const reason = read.reason ?? (unchanged ? "No changes yet" : undefined);

  const save = () => {
    const food = read.food;
    if (!food) return;
    const out: { id: string | null } = { id: null };
    update((s) => {
      out.id = initial ? editCustomFood(s, initial.id, food) : addCustomFood(s, food);
    });
    const id = out.id;
    if (id === null) {
      showToast("That food has changed elsewhere");
      onClose();
      return;
    }
    showToast("Saved");
    onSaved?.(id);
    onClose();
  };

  const remove = () => {
    if (!initial) return;
    update((s) => removeCustomFood(s, initial.id));
    showToast(`Deleted ${initial.name}`);
    setAsking(false);
    onClose();
  };

  const fig = read.food
    ? (() => {
        const { per100, g: grams } = read.food;
        const x = grams / 100;
        return `One ${read.food.unit}: ${Math.round(per100.k * x).toLocaleString()} kcal · P ${one(per100.p * x)} · C ${one(per100.c * x)} · F ${one(per100.f * x)} g`;
      })()
    : null;

  if (asking && initial) {
    return (
      <ConfirmSheet
        title={`Delete ${initial.name}?`}
        sub="What you already logged from it stays as it is."
        confirm={{ label: "Delete food", onClick: remove }}
        onClose={() => setAsking(false)}
      />
    );
  }

  return (
    <Sheet
      title={initial ? "Edit food" : "Add a food"}
      sub={initial ? "Changes apply to what you log from now on" : "Kept with your account, on every phone you sign in on"}
      onClose={onClose}
      footer={
        <ActionBar
          inSheet
          secondary={{ label: "Cancel", onClick: onClose }}
          primary={{
            label: "Save food",
            icon: I.check,
            onClick: save,
            disabled: reason !== undefined,
            reason,
          }}
        />
      }
    >
      <div className="k-fields">
        {kindSwitch}
        <Field label="Name" type="text" value={d.name} onChange={(v) => set("name", v)} autoComplete="off" />
        <div className="k-field">
          <FieldGrid>
            <Field
              label="Serving"
              type="text"
              value={d.unit}
              placeholder="pack, katori, scoop"
              onChange={(v) => set("unit", v)}
              autoComplete="off"
            />
            <Field label="Weighs" type="number" unit="g" min="0" step="1" value={d.g} onChange={(v) => set("g", v)} />
          </FieldGrid>
          <p className="k-field__hint">The unit you count in, and what one weighs.</p>
        </div>

        <ChoiceField label="Values per">
          <Segmented
            ariaLabel="Values per"
            value={d.per}
            onChange={(v) => set("per", v)}
            options={[
              { value: "100g", label: "100 g" },
              { value: "serving", label: "serving" },
            ]}
          />
        </ChoiceField>

        <FieldGrid>
          {CUSTOM_REQUIRED.map((k) => (
            <Field
              key={k}
              label={LABEL[k].label}
              unit={LABEL[k].unit}
              type="number"
              min="0"
              step="any"
              value={d.values[k] ?? ""}
              onChange={(v) => setV(k, v)}
            />
          ))}
        </FieldGrid>

        <FieldSet legend="If the label gives them" hint="Leave a value blank if the label does not give it.">
          <FieldGrid>
            {CUSTOM_OPTIONAL.map((k) => (
              <Field
                key={k}
                label={LABEL[k].label}
                unit={LABEL[k].unit}
                type="number"
                min="0"
                step="any"
                placeholder="–"
                value={d.values[k] ?? ""}
                onChange={(v) => setV(k, v)}
              />
            ))}
          </FieldGrid>
        </FieldSet>

        {fig ? <p className="k-fields__hint k-fields__hint--fig">{fig}</p> : null}

        {initial ? (
          <RowGroup>
            <NavRow kind="danger" icon={I.trash} title="Delete this food" onClick={() => setAsking(true)} />
          </RowGroup>
        ) : null}
      </div>
    </Sheet>
  );
}
